/**
 * OTS Theme Configuration
 *
 * This file contains all theme color configurations for the application.
 * To change colors, simply modify the values below.
 *
 * Each theme has:
 * - name: Display name of the theme
 * - primary: Main accent color (buttons, links, active states)
 * - primaryHover: Darker shade for hover states
 * - secondary: Gradient end color for buttons
 * - gradient: Background gradient for auth pages
 * - lightBg: Light background color for badges/chips
 * - navBg: Navbar background color
 */

export const themes = {
  ocean: {
    name: "Ocean",
    type: "gradient",
    primary: "#11998e",
    primaryHover: "#0d7a6e",
    secondary: "#38ef7d",
    gradient: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    lightBg: "#e8f5f3",
    navBg: "#1a1a2e",
  },
  sunset: {
    name: "Sunset",
    type: "gradient",
    primary: "#f2709c",
    primaryHover: "#d85d87",
    secondary: "#ff9472",
    gradient: "linear-gradient(135deg, #2c1654 0%, #4a1942 50%, #6b2737 100%)",
    lightBg: "#fce4ec",
    navBg: "#2c1654",
  },
  aurora: {
    name: "Aurora",
    type: "gradient",
    primary: "#667eea",
    primaryHover: "#5a6fd6",
    secondary: "#764ba2",
    gradient: "linear-gradient(135deg, #1a1a3e 0%, #2d2a4a 50%, #3d3356 100%)",
    lightBg: "#eef1fd",
    navBg: "#1a1a3e",
  },
  forest: {
    name: "Forest",
    type: "gradient",
    primary: "#56ab2f",
    primaryHover: "#4a9429",
    secondary: "#a8e063",
    gradient: "linear-gradient(135deg, #1a2a1a 0%, #1e3620 50%, #234028 100%)",
    lightBg: "#e8f5e9",
    navBg: "#1a2a1a",
  },
  midnight: {
    name: "Midnight",
    type: "gradient",
    primary: "#6c5ce7",
    primaryHover: "#5f4dd0",
    secondary: "#a29bfe",
    gradient: "linear-gradient(135deg, #0c0c1e 0%, #1a1a3a 50%, #252552 100%)",
    lightBg: "#ede7f6",
    navBg: "#0c0c1e",
  },
  royal: {
    name: "Royal",
    type: "gradient",
    primary: "#c39b3b",
    primaryHover: "#a8842e",
    secondary: "#f5d67b",
    gradient: "linear-gradient(135deg, #14142b 0%, #1f1f45 50%, #2b2450 100%)",
    lightBg: "#fbf3de",
    navBg: "#14142b",
  },
  candy: {
    name: "Candy",
    type: "gradient",
    primary: "#e84393",
    primaryHover: "#cf3781",
    secondary: "#fd79a8",
    gradient: "linear-gradient(135deg, #2b0f2b 0%, #451a3e 50%, #5d2450 100%)",
    lightBg: "#fde7f1",
    navBg: "#2b0f2b",
  },
  slate: {
    name: "Slate",
    type: "gradient",
    primary: "#3b82f6",
    primaryHover: "#2f6ad4",
    secondary: "#60a5fa",
    gradient: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
    lightBg: "#e8f1fe",
    navBg: "#0f172a",
  },
  citrus: {
    name: "Citrus",
    type: "gradient",
    primary: "#f97316",
    primaryHover: "#e0630f",
    secondary: "#fbbf24",
    gradient: "linear-gradient(135deg, #2a1608 0%, #3d2110 50%, #52301a 100%)",
    lightBg: "#fff2e3",
    navBg: "#2a1608",
  },

  // Solid, single-color themes (no gradient swatch) for users who prefer a flat look.
  green: {
    name: "Green",
    type: "solid",
    primary: "#22c55e",
    primaryHover: "#16a34a",
    secondary: "#22c55e",
    gradient: "linear-gradient(135deg, #0b1f14 0%, #123021 50%, #1a4530 100%)",
    lightBg: "#e8f9ee",
    navBg: "#0b1f14",
  },
  blue: {
    name: "Blue",
    type: "solid",
    primary: "#2563eb",
    primaryHover: "#1d4ed8",
    secondary: "#2563eb",
    gradient: "linear-gradient(135deg, #0a1330 0%, #11204a 50%, #182d63 100%)",
    lightBg: "#e7edfd",
    navBg: "#0a1330",
  },
  pink: {
    name: "Pink",
    type: "solid",
    primary: "#ec4899",
    primaryHover: "#db2777",
    secondary: "#ec4899",
    gradient: "linear-gradient(135deg, #2b0f1f 0%, #451a33 50%, #5d2446 100%)",
    lightBg: "#fde6f0",
    navBg: "#2b0f1f",
  },
  purple: {
    name: "Purple",
    type: "solid",
    primary: "#8b5cf6",
    primaryHover: "#7c3aed",
    secondary: "#8b5cf6",
    gradient: "linear-gradient(135deg, #190f33 0%, #27184f 50%, #352069 100%)",
    lightBg: "#f1ebfe",
    navBg: "#190f33",
  },
  red: {
    name: "Red",
    type: "solid",
    primary: "#ef4444",
    primaryHover: "#dc2626",
    secondary: "#ef4444",
    gradient: "linear-gradient(135deg, #2a0d0d 0%, #421414 50%, #591c1c 100%)",
    lightBg: "#fde8e8",
    navBg: "#2a0d0d",
  },
  teal: {
    name: "Teal",
    type: "solid",
    primary: "#14b8a6",
    primaryHover: "#0f9c8c",
    secondary: "#14b8a6",
    gradient: "linear-gradient(135deg, #07211d 0%, #0d332c 50%, #13453c 100%)",
    lightBg: "#e3f8f5",
    navBg: "#07211d",
  },
  amber: {
    name: "Amber",
    type: "solid",
    primary: "#f59e0b",
    primaryHover: "#d97f06",
    secondary: "#f59e0b",
    gradient: "linear-gradient(135deg, #2b1e05 0%, #41300a 50%, #57410f 100%)",
    lightBg: "#fef3e0",
    navBg: "#2b1e05",
  },
  graphite: {
    name: "Graphite",
    type: "solid",
    primary: "#475569",
    primaryHover: "#334155",
    secondary: "#475569",
    gradient: "linear-gradient(135deg, #0f1419 0%, #1a2028 50%, #252c38 100%)",
    lightBg: "#eef1f4",
    navBg: "#0f1419",
  },
};

// Default theme
export const DEFAULT_THEME = "ocean";

/**
 * User Type Colors
 * Different colors for landlord and tenant to visually distinguish them
 */
export const userTypeColors = {
  landlord: {
    primary: "#11998e",
    secondary: "#38ef7d",
    gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
    lightBg: "#e8f5f3",
    icon: "#11998e",
  },
  tenant: {
    primary: "#667eea",
    secondary: "#764ba2",
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    lightBg: "#eef1fd",
    icon: "#667eea",
  },
};

// LocalStorage key for saving theme preference
export const THEME_STORAGE_KEY = "ots_theme";

/**
 * Get theme by name
 * @param {string} themeName - Name of the theme
 * @returns {object} Theme object
 */
export const getTheme = (themeName) => {
  return themes[themeName] || themes[DEFAULT_THEME];
};

/**
 * Get all theme names
 * @returns {string[]} Array of theme names
 */
export const getThemeNames = () => {
  return Object.keys(themes);
};

/**
 * Resolve a theme-colored background: for a "solid" theme this always
 * collapses to one flat color (no gradient, no design), while a "gradient"
 * theme keeps whatever gradient CSS was passed in untouched.
 * @param {object} theme - A theme object from `themes`
 * @param {string} gradientCss - The gradient `background` value to use when the theme is a gradient theme
 * @param {string} [solidCss] - The flat color/value to use when the theme is solid (defaults to theme.primary)
 * @returns {string}
 */
export const themeBg = (theme, gradientCss, solidCss) =>
  theme?.type === "solid" ? solidCss ?? theme.primary : gradientCss;

/**
 * SVG Pattern for background
 */
export const backgroundPattern = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;